import React from 'react';

import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import InvertColorsRoundedIcon from '@mui/icons-material/InvertColorsRounded';
import * as yup from 'yup';

import { ReactComponent as DesktopIcon } from '../assets/images/icon-computer-screen.svg';
import { ReactComponent as MobileIcon } from '../assets/images/icon-mobile-phone.svg';
import { ReactComponent as RedoIcon } from '../assets/images/icon-redo.svg';
import { ReactComponent as UndoIcon } from '../assets/images/icon-undo.svg';

import variables from '../contexts/ThemeProvider/variables';
import { ImageFitEnum } from '../types/shell';
import {
  ClaimLinkEditorPageEnum,
  ClaimLinkEditorViewTypeEnum,
  ClaimLinkTemplateSourceEnum,
  ClaimLinkTemplateStatusEnum,
  EmailTemplateTypesEnum,
  IBuilderActionButtonConfig,
  IClaimLinkBuilderValues,
  IClaimLinkPageOverrides,
  IClaimLinkTemplate,
  ITemplateCardConfig,
  ITemplateModalStep,
  IValidatedFieldConfig,
  TCollectiveEmailTemplate,
  TCollectiveNoteTemplate,
  TemplateModalStepEnum,
  TemplateTypesEnum,
} from '../types/templates';
import { BackgroundTypesEnum, ImageSizeEnum } from '../types/upload';
import { hexToRgbColor, parseRgbFromScss } from '../utils/helpers';
import { NO_DEPARTMENT } from './organizations';
import { URL_VARS } from './routing';
import {
  RECEIVER_COMPANY_NAME,
  RECEIVER_FIRST_NAME,
  RECEIVER_LAST_NAME,
  SENDER_COMPANY_NAME,
  SENDER_EMAIL,
  SENDER_FIRST_NAME,
  SENDER_LAST_NAME,
  SENDER_PHONE,
} from './shipping';
import { SIGNATURE } from './signature';
import {
  BODY,
  COVER_IMAGE,
  DEPARTMENT_IDS,
  DIGITAL_GIFT_NAMES,
  EMAIL_MESSAGE,
  EMAIL_SUBJECT,
  EMAIL_TEMPLATE_ID,
  GLOBAL_APP_BAR_LOGO_IMAGE,
  GLOBAL_HEADER_MEDIA,
  GLOBAL_HERO_SECTION_IMAGE,
  IS_DEFAULT,
  MESSAGE,
  NOTE_TEMPLATE_ID,
  NO_TEMPLATE,
  RECEIVER_EMAIL,
  SUBJECT,
  TEMPLATE_TYPE,
  TITLE,
  TYPE,
} from './templateKeys';

export * from './templateKeys';

export const TEMPLATE_LABEL = 'template';
export const MAX_TEMPLATES_PER_PAGE = 50;

export const INITIAL_ADD_NOTE_FORM_STATE = {
  [MESSAGE]: '',
  [NOTE_TEMPLATE_ID]: '',
};

export const INITIAL_ADD_EMAIL_FORM_STATE = {
  [EMAIL_TEMPLATE_ID]: '',
  [EMAIL_MESSAGE]: '',
  [EMAIL_SUBJECT]: '',
  [RECEIVER_EMAIL]: '',
};

export const EMPTY_NOTE_TEMPLATE = {
  item_id: NO_TEMPLATE,
  body: '',
  title: 'No template',
};

export const EMPTY_EMAIL_TEMPLATE = {
  item_id: NO_TEMPLATE,
  body: '',
  subject: 'No template',
};

export const MAX_NEW_NOTE_LENGTH = 300;
export const MAX_NOTE_LENGTH = 250;
export const MAX_BULK_NOTE_LENGTH = 300;
export const MAX_ONE_LINK_NOTE_LENGTH = 300;
export const WARNING_RANGE = 20;

export const ADD_NOTE_VALIDATION_SCHEMA = yup
  .object()
  .shape({
    [MESSAGE]: yup.string().max(MAX_NOTE_LENGTH),
  })
  .required();

export const ADD_BULK_NOTE_VALIDATION_SCHEMA = yup
  .object()
  .shape({
    [MESSAGE]: yup.string().max(MAX_BULK_NOTE_LENGTH),
  })
  .required();

export const ADD_ONE_LINK_NOTE_VALIDATION_SCHEMA = yup
  .object()
  .shape({
    [MESSAGE]: yup.string().max(MAX_ONE_LINK_NOTE_LENGTH),
  })
  .required();

export const EMAIL_MESSAGE_REGEX = /\[\bDelayedShippingURLs\]|\[DigitalGiftURLs\b\]/g;
export const EMAIL_DIGITAL_GIFT_MESSAGE_REGEX = /\[DigitalGiftURLs]/g;
export const EMAIL_DELAYED_SHIPPING_MESSAGE_REGEX = /\[DelayedShippingURLs]/g;

export const NO_REQUIRED_PHYSICAL_TAG_ERROR_MESSAGE = 'Your email text must contain [DelayedShippingURLs] token';
export const NO_REQUIRED_DIGITAL_TAG_ERROR_MESSAGE = 'Your email text must contain [DigitalGiftURLs] token';

export const emailMessageValidation = (isDigital: boolean) =>
  yup
    .string()
    .required()
    .matches(
      EMAIL_MESSAGE_REGEX,
      isDigital ? NO_REQUIRED_DIGITAL_TAG_ERROR_MESSAGE : NO_REQUIRED_PHYSICAL_TAG_ERROR_MESSAGE,
    );

export const ADD_EMAIL_VALIDATION_SCHEMA = (isDigital: boolean) =>
  yup
    .object()
    .shape({
      [EMAIL_MESSAGE]: emailMessageValidation(isDigital),
      [RECEIVER_EMAIL]: yup.string().email().required(),
      [EMAIL_SUBJECT]: yup.string().required(),
    })
    .required();

export const ADD_BULK_EMAIL_VALIDATION_SCHEMA = (isDigital: boolean) =>
  yup
    .object()
    .shape({
      [EMAIL_MESSAGE]: emailMessageValidation(isDigital),
      [EMAIL_SUBJECT]: yup.string().required(),
    })
    .required();

export const ADD_NOTE_TEMPLATE_VALIDATION_SCHEMA = yup.object().shape({
  [BODY]: yup.string().max(MAX_NEW_NOTE_LENGTH).required(),
  [DEPARTMENT_IDS]: yup.array().of(yup.string()).required(),
  [TITLE]: yup.string().required(),
  [IS_DEFAULT]: yup.boolean(),
  [TEMPLATE_TYPE]: yup.string().oneOf([TemplateTypesEnum.Note, TemplateTypesEnum.Email]).required(),
});

export const ADD_EMAIL_TEMPLATE_VALIDATION_SCHEMA = yup.object().shape({
  [BODY]: yup
    .string()
    .required()
    .when(TYPE, {
      is: EmailTemplateTypesEnum.DELAYED_SHIPPING,
      then: (sc) => sc.matches(EMAIL_DELAYED_SHIPPING_MESSAGE_REGEX),
      otherwise: (sc) => sc.matches(EMAIL_DIGITAL_GIFT_MESSAGE_REGEX),
    }),
  [DEPARTMENT_IDS]: yup.array().of(yup.string()).required(),
  [SUBJECT]: yup.string().required(),
  [TYPE]: yup.string().oneOf([EmailTemplateTypesEnum.DELAYED_SHIPPING, EmailTemplateTypesEnum.DIGITAL_GIFT]).required(),
  [IS_DEFAULT]: yup.boolean(),
  [TEMPLATE_TYPE]: yup.string().oneOf([TemplateTypesEnum.Note, TemplateTypesEnum.Email]).required(),
});

export const INITIAL_TEMPLATE_NOTE_FORM_STATE: Partial<TCollectiveNoteTemplate> = {
  [BODY]: '',
  [DEPARTMENT_IDS]: [NO_DEPARTMENT],
  [TITLE]: '',
  [IS_DEFAULT]: false,
  [TEMPLATE_TYPE]: TemplateTypesEnum.Note,
};

export const INITIAL_TEMPLATE_EMAIL_FORM_STATE: Partial<TCollectiveEmailTemplate> = {
  [BODY]: '',
  [DEPARTMENT_IDS]: [NO_DEPARTMENT],
  [SUBJECT]: '',
  [TYPE]: EmailTemplateTypesEnum.DELAYED_SHIPPING,
  [IS_DEFAULT]: false,
  [TEMPLATE_TYPE]: TemplateTypesEnum.Email,
};

export const TEMPLATE_TOKENS_REGEX = /\[.*?\]/gm;

export const TEMPLATE_TOKENS = {
  ReceiverFirstName: 'Receiver.FirstName',
  ReceiverLastName: 'Receiver.LastName',
  ReceiverCompanyName: 'Receiver.CompanyName',
  SenderFirstName: 'Sender.FirstName',
  SenderLastName: 'Sender.LastName',
  SenderCompanyName: 'Sender.CompanyName',
  SenderEmail: 'Sender.Email',
  SenderPhone: 'Sender.Phone',
  SenderSignature: 'Sender.Signature',
  DigitalGiftNames: 'DigitalGiftNames',
  DigitalGiftURLs: 'DigitalGiftURLs',
  DelayedShippingURLs: 'DelayedShippingURLs',
};

export const TAG_PICKER_AVAILABLE_TAGS = [
  TEMPLATE_TOKENS.SenderFirstName,
  TEMPLATE_TOKENS.SenderLastName,
  TEMPLATE_TOKENS.SenderCompanyName,
  TEMPLATE_TOKENS.ReceiverFirstName,
  TEMPLATE_TOKENS.ReceiverLastName,
];

export const TOKENS_MAP = {
  [TEMPLATE_TOKENS.ReceiverFirstName]: RECEIVER_FIRST_NAME,
  [TEMPLATE_TOKENS.ReceiverLastName]: RECEIVER_LAST_NAME,
  [TEMPLATE_TOKENS.ReceiverCompanyName]: RECEIVER_COMPANY_NAME,
  [TEMPLATE_TOKENS.SenderFirstName]: SENDER_FIRST_NAME,
  [TEMPLATE_TOKENS.SenderLastName]: SENDER_LAST_NAME,
  [TEMPLATE_TOKENS.SenderCompanyName]: SENDER_COMPANY_NAME,
  [TEMPLATE_TOKENS.SenderEmail]: SENDER_EMAIL,
  [TEMPLATE_TOKENS.SenderPhone]: SENDER_PHONE,
  [TEMPLATE_TOKENS.SenderSignature]: SIGNATURE,
  [TEMPLATE_TOKENS.DigitalGiftNames]: DIGITAL_GIFT_NAMES,
};

export const TemplateSuccessToastMessages = {
  [URL_VARS.NEW]: 'New template was added',
  [URL_VARS.EDIT]: 'Template was edited',
};

export const EMAIL_TEMPLATE_TYPES = [
  {
    label: 'Claim link',
    value: EmailTemplateTypesEnum.DELAYED_SHIPPING,
  },
  {
    label: 'Digital gift',
    value: EmailTemplateTypesEnum.DIGITAL_GIFT,
  },
];

export const TemplateModalSteps: ITemplateModalStep[] = [
  {
    id: TemplateModalStepEnum.Details,
    label: 'Details',
    value: 'details',
  },
  {
    id: TemplateModalStepEnum.Branding,
    label: 'Branding',
    value: 'branding',
  },
  {
    id: TemplateModalStepEnum.HeroDefaults,
    label: 'Hero defaults',
    value: 'hero-defaults',
  },
];

export const TemplateModalValidationSchema = yup.object({
  templateName: yup.string().required('Template name is required'),
});

export const DEFAULT_BUTTON_COLOR = parseRgbFromScss(variables.defaultAccentColor);

export const DEFAULT_WHITE_COLOR = { r: 255, g: 255, b: 255, a: 1 };

export const BUILDER_EDIT_PAGE_TABS = [
  {
    label: 'Pick your gift',
    value: ClaimLinkEditorPageEnum.PickYourGift,
    dataAction: 'editor_tab_pick_your_gift',
  },
  {
    label: 'Product options',
    value: ClaimLinkEditorPageEnum.ProductOptions,
    dataAction: 'editor_tab_product_options',
  },
  {
    label: 'Shipping details',
    value: ClaimLinkEditorPageEnum.ShippingDetails,
    dataAction: 'editor_tab_shipping_details',
  },
];

export const BUILDER_ACTION_BUTTONS: IBuilderActionButtonConfig[] = [
  {
    label: 'Save draft',
    variant: 'outlined',
    dataAction: 'save_draft_btn',
    visibleWhen: ClaimLinkTemplateStatusEnum.Draft,
  },
  {
    label: 'Publish changes',
    variant: 'contained',
    dataAction: 'publish_changes_btn',
  },
];

export const TEMPLATE_PAGE_NAMES: Record<string, string> = {
  pick_your_gift: 'Pick Your Gift',
  pick_your_option: 'Product Options',
  shipping_page: 'Shipping Details',
};

export const BUILDER_VIEW_TYPE_TABS = [
  {
    label: 'Desktop',
    value: ClaimLinkEditorViewTypeEnum.Desktop,
    dataAction: 'editor_view_type_desktop_btn',
  },
  {
    label: 'Mobile',
    value: ClaimLinkEditorViewTypeEnum.Mobile,
    dataAction: 'editor_view_type_mobile_btn',
  },
];

export const VIEW_TYPE_ICONS: Record<ClaimLinkEditorViewTypeEnum, React.FC<React.SVGProps<SVGSVGElement>>> = {
  [ClaimLinkEditorViewTypeEnum.Desktop]: DesktopIcon,
  [ClaimLinkEditorViewTypeEnum.Mobile]: MobileIcon,
};

export const UNDO = 'Undo';
export const REDO = 'Redo';

export const BUILDER_STEP_CONTROLS_BUTTONS = [
  {
    label: UNDO,
    icon: UndoIcon,
    dataAction: 'editor_step_controls_undo_btn',
  },
  {
    label: 'Redo',
    icon: RedoIcon,
    dataAction: 'editor_step_controls_redo_btn',
  },
];

// Template builder form field names
export const TEMPLATE_NAME = 'templateName';
export const SELECTED_DEPARTMENT_IDS = 'selectedDepartmentIds';
export const STATUS = 'status';

// Global field constants
export const GLOBAL_LOGO_HREF = 'globalLogoImageHref';
export const GLOBAL_APP_BAR_LOGO_SIZE = 'globalLogoImageSize';
export const GLOBAL_APP_BAR_BG_COLOR = 'globalAppBarBackgroundColor';
export const GLOBAL_HERO_SECTION_BG_COLOR = 'globalHeroSectionBackgroundColor';
export const GLOBAL_HERO_SECTION_POSITION = 'globalHeroSectionImagePosition';
export const GLOBAL_HERO_SECTION_FIT = 'globalHeroSectionImageFit';
export const GLOBAL_HERO_SECTION_BG_TYPE = 'globalHeroSectionBackgroundType';
export const GLOBAL_ACCENT_COLOR = 'globalAccentColor';
export const GLOBAL_BUTTON_RADIUS = 'globalButtonRadius';
export const BACKGROUND_COLOR = 'backgroundColor';
export const ACTIVE_PAGE_ID = 'activePageId';
export const PAGES = 'pages';

export const HEADER_TEXT = 'headerText';
export const BODY_TEXT = 'bodyText';
export const LOGO_IMAGE = 'logoImageUrl';
export const LOGO_IMAGE_SIZE = 'logoImageSize';
export const HEADER_MEDIA = 'headerMediaUrl';

/*
 * App bar logo size, expressed as a percent in the UI and persisted as a 0.5-2 float ratio.
 * The slider colour is the fixed IEX builder chrome primary - it must never follow the
 * user-configurable experience button colour (EX-6266).
 */
export const LOGO_SIZE_MIN = 50;
export const LOGO_SIZE_MAX = 200;
export const LOGO_SIZE_STEP = 5;
export const LOGO_SIZE_DEFAULT = 100;
export const BUILDER_CHROME_PRIMARY = '#5F11EA';
export const BUILDER_CHROME_PRIMARY_HOVER = '#3E00DC';
export const BUILDER_CHROME_PRIMARY_RING = 'rgba(95,17,234,0.16)';
export const BUILDER_CHROME_PRIMARY_RAIL = 'rgba(95,17,234,0.26)';
export const HEADER_MEDIA_EMPTY = 'empty';
// Visually-empty rich text saved/sent for an intentionally cleared header/body. A bare empty
// string is treated as "not set" downstream (claim page falls back to its hardcoded defaults),
// so a cleared field must round-trip as this sentinel instead.
export const EMPTY_RICH_TEXT_HTML = '<div></div>';
export const BACKGROUND_TYPE = 'backgroundType';
export const BACKGROUND_IMAGE_SIZE = 'backgroundImageSize';
export const ACCENT_COLOR = 'buttonColor';
export const BACKGROUND_IMAGE = 'backgroundImageUrl';
export const APP_BAR_BACKGROUND_COLOR = 'appBarBackgroundColor';
export const HERO_SECTION_BACKGROUND_COLOR = 'heroSectionBackgroundColor';
export const HERO_SECTION_IMAGE_URL = 'heroSectionImageUrl';
export const HERO_SECTION_IMAGE_FIT = 'heroSectionImageFit';
export const HERO_SECTION_BACKGROUND_TYPE = 'heroSectionBackgroundType';

// Hero height modes ride on the existing image_fit field (no separate backend key):
// Cover = Auto (content-driven height with a fixed floor), Contain = Adapt (the claim
// page grows the hero to the image aspect and measures that aspect itself).
export const HERO_HEIGHT_MODE_TABS = [
  {
    label: 'Auto',
    value: ImageFitEnum.Cover,
  },
  {
    label: 'Adapt to image',
    value: ImageFitEnum.Contain,
  },
];

export const INITIAL_PAGE_OVERRIDES: IClaimLinkPageOverrides = {
  headerMediaUrl: undefined,
  logoImageUrl: undefined,
  logoImageSize: undefined,
  appBarBackgroundColor: undefined,
  heroSectionBackgroundColor: undefined,
  heroSectionImageUrl: undefined,
  heroSectionImageFit: undefined,
  heroSectionBackgroundType: undefined,
  buttonColor: undefined,
};

export const getInitialTemplateBuilderValues = (
  blueprint?: Partial<IClaimLinkTemplate> | null,
  organizationLogoUrl?: string,
): IClaimLinkBuilderValues => {
  const defaultButtonColor = hexToRgbColor(blueprint?.buttons?.action?.color, DEFAULT_BUTTON_COLOR);

  return {
    // not from blueprint
    [TEMPLATE_NAME]: '',
    [SELECTED_DEPARTMENT_IDS]: [NO_DEPARTMENT],
    [STATUS]: ClaimLinkTemplateStatusEnum.Draft,
    [COVER_IMAGE]: '',

    // Global defaults FROM BLUEPRINT (and organization)
    [GLOBAL_APP_BAR_LOGO_IMAGE]: organizationLogoUrl || blueprint?.header?.app_bar?.logo_image_url || '',
    // Ratio -> percent inline: constants/ must not import from utils/ (circular dependency)
    [GLOBAL_APP_BAR_LOGO_SIZE]: blueprint?.header?.app_bar?.logo_image_size
      ? Math.max(LOGO_SIZE_MIN, Math.min(LOGO_SIZE_MAX, Math.round(blueprint.header.app_bar.logo_image_size * 100)))
      : LOGO_SIZE_DEFAULT,
    [GLOBAL_LOGO_HREF]: blueprint?.header?.app_bar?.logo_image_href || '',
    [GLOBAL_APP_BAR_BG_COLOR]: hexToRgbColor(blueprint?.header?.app_bar?.background_color, DEFAULT_BUTTON_COLOR),
    [GLOBAL_HEADER_MEDIA]: blueprint?.header?.media?.image_url || '',
    [GLOBAL_HERO_SECTION_BG_COLOR]: hexToRgbColor(
      blueprint?.header?.hero_section?.background_color,
      DEFAULT_BUTTON_COLOR,
    ),
    [GLOBAL_HERO_SECTION_IMAGE]: blueprint?.header?.hero_section?.image_url || '',
    [GLOBAL_HERO_SECTION_POSITION]: blueprint?.header?.hero_section?.image_position || 'center',
    [GLOBAL_HERO_SECTION_FIT]: blueprint?.header?.hero_section?.image_fit || ImageFitEnum.Cover,
    [GLOBAL_HERO_SECTION_BG_TYPE]: blueprint?.header?.hero_section?.image_url
      ? BackgroundTypesEnum.Image
      : BackgroundTypesEnum.Color,
    [GLOBAL_ACCENT_COLOR]: defaultButtonColor,
    [GLOBAL_BUTTON_RADIUS]: blueprint?.buttons?.action?.radius ?? 8,
    [BACKGROUND_COLOR]: hexToRgbColor(blueprint?.background?.color, DEFAULT_BUTTON_COLOR),

    // Per-page data
    [ACTIVE_PAGE_ID]: ClaimLinkEditorPageEnum.PickYourGift,
    [PAGES]: {
      [ClaimLinkEditorPageEnum.PickYourGift]: {
        headerText: blueprint?.pages?.pick_your_gift?.header_text || '',
        bodyText: blueprint?.pages?.pick_your_gift?.body_text || '',
        overrides: { ...INITIAL_PAGE_OVERRIDES },
      },
      [ClaimLinkEditorPageEnum.ProductOptions]: {
        headerText: blueprint?.pages?.pick_your_option?.header_text || '',
        bodyText: blueprint?.pages?.pick_your_option?.body_text || '',
        overrides: { ...INITIAL_PAGE_OVERRIDES },
      },
      [ClaimLinkEditorPageEnum.ShippingDetails]: {
        headerText: blueprint?.pages?.shipping_page?.header_text || '',
        bodyText: blueprint?.pages?.shipping_page?.body_text || '',
        overrides: { ...INITIAL_PAGE_OVERRIDES },
      },
    },

    // DEPRECATED - keeping for backward compatibility
    [HEADER_TEXT]: '',
    [BODY_TEXT]: '',
    [LOGO_IMAGE]: organizationLogoUrl || blueprint?.header?.app_bar?.logo_image_url || null,
    [HEADER_MEDIA]: blueprint?.header?.media?.image_url || null,
    [BACKGROUND_TYPE]: BackgroundTypesEnum.Color,
    [BACKGROUND_IMAGE_SIZE]: ImageSizeEnum.Fill,
    [ACCENT_COLOR]: defaultButtonColor,
    [BACKGROUND_IMAGE]: null,
  };
};

export const INITIAL_TEMPLATE_BUILDER_VALUES: IClaimLinkBuilderValues = getInitialTemplateBuilderValues();

export const BACKGROUND_TYPE_TABS = [
  {
    label: BackgroundTypesEnum.Color,
    icon: InvertColorsRoundedIcon,
    value: BackgroundTypesEnum.Color,
  },
  {
    label: BackgroundTypesEnum.Image,
    icon: AddPhotoAlternateOutlinedIcon,
    value: BackgroundTypesEnum.Image,
  },
];

export const CLAIM_LINK_TEMPLATE_CARD_MAX_WIDTH = 320;
export const CLAIM_LINK_TEMPLATE_CARD_THUMBNAIL_ASPECT_RATIO = '275 / 155';

// "Choose template" modal sizing: clamps to these maxima, then to the viewport minus the margin.
export const CHOOSE_TEMPLATE_MODAL_MAX_WIDTH = 1080;
export const CHOOSE_TEMPLATE_MODAL_MAX_HEIGHT = 965;
export const MODAL_VIEWPORT_MARGIN = 64;

export const SIDE_PANEL_DEFAULT_WIDTH = 360;
// Fits at least 5 lines of text plus the toolbar before the editor scrolls.
export const SIDE_PANEL_EDITOR_DEFAULT_HEIGHT = 245;

export const DEFAULT_TEMPLATE_CARD_CONFIG: ITemplateCardConfig = {
  showStatusChip: true,
  actions: undefined,
  enableEditDetails: true,
  highlightDefault: false,
};

export const ZOOM_VALUES = {
  Small: 50,
  Normal: 100,
  Large: 200,
};

export const BUILDER_PAGE_HISTORY_LIMIT = 50;

/**
 * Registry of all validated fields in the template builder
 * Add new fields here to enable validation error clearing
 */
export const VALIDATED_FIELDS_CONFIG: IValidatedFieldConfig[] = [
  // Page-specific text fields
  {
    fieldConstant: HEADER_TEXT,
    errorPath: 'pages.{pageKey}.header_text',
  },
  {
    fieldConstant: BODY_TEXT,
    errorPath: 'pages.{pageKey}.body_text',
  },

  // Page-specific media fields
  {
    fieldConstant: HEADER_MEDIA,
    errorPath: 'pages.{pageKey}.header_media_url',
  },

  // Page-specific color fields
  {
    fieldConstant: ACCENT_COLOR,
    errorPath: 'pages.{pageKey}.button_color',
  },
  {
    fieldConstant: APP_BAR_BACKGROUND_COLOR,
    errorPath: 'pages.{pageKey}.app_bar_background_color',
  },
  {
    fieldConstant: HERO_SECTION_BACKGROUND_COLOR,
    errorPath: 'pages.{pageKey}.hero_section_background_color',
  },

  // Page-specific image fields
  {
    fieldConstant: HERO_SECTION_IMAGE_URL,
    errorPath: 'pages.{pageKey}.hero_section_image_url',
  },
  {
    fieldConstant: HERO_SECTION_IMAGE_FIT,
    errorPath: 'pages.{pageKey}.hero_section_image_fit',
  },

  // Page-specific logo (override)
  {
    fieldConstant: LOGO_IMAGE,
    errorPath: 'pages.{pageKey}.logo_image_url',
  },

  // Page-specific app bar logo size (override)
  {
    fieldConstant: LOGO_IMAGE_SIZE,
    errorPath: 'pages.{pageKey}.logo_image_size',
  },
];

/**
 * Mapping from ClaimLinkEditorPageEnum to backend page keys
 * Used for constructing error field keys
 */
export const CLAIM_LINK_TEMPLATE_STATUS_OPTIONS = [
  { label: 'Live', value: ClaimLinkTemplateStatusEnum.Live },
  { label: 'Hidden', value: ClaimLinkTemplateStatusEnum.Hidden },
  { label: 'Draft', value: ClaimLinkTemplateStatusEnum.Draft },
];

export const PAGE_KEY_MAP: Record<ClaimLinkEditorPageEnum, string> = {
  [ClaimLinkEditorPageEnum.PickYourGift]: 'pick_your_gift',
  [ClaimLinkEditorPageEnum.ProductOptions]: 'pick_your_option',
  [ClaimLinkEditorPageEnum.ShippingDetails]: 'shipping_page',
};

export const INITIAL_PAGE_SIZE_MODAL = 6;
export const INFINITE_PAGE_SIZE_MODAL = 12;
export const HEADER_TEXT_REQUIRED_ERROR = 'Header text on any page is a required field.';

export const MOBILE_VIEW_WIDTH = 390;

export const BASE_SCALE = 0.8;

export const LEGACY_CLAIM_PAGE_SETTINGS_ALERT =
  'Legacy campaign settings are available and will apply to the claim page. Select a template to apply the new settings.';

export const ALERT_CONTENT: Record<ClaimLinkTemplateSourceEnum, { title: string; message: string }> = {
  [ClaimLinkTemplateSourceEnum.Campaign]: {
    title: 'Campaign default applied',
    message:
      'This send uses the experience from the selected campaign. Any changes made to the campaign settings will apply to this send.',
  },
  [ClaimLinkTemplateSourceEnum.Org]: {
    title: 'Organization default applied',
    message:
      'This send uses the experience from your organization. Any changes made to the organization settings will apply to this send.',
  },
  [ClaimLinkTemplateSourceEnum.Send]: {
    title: 'Custom Experience',
    message:
      'This send uses its own custom experience. Changes to campaign or organization settings will not affect this send.',
  },
};

export const TEMPLATE_CARD_CONFIG: ITemplateCardConfig = {
  actions: () => null,
  highlightDefault: false,
  showStatusChip: false,
  showDefaultBanner: false,
  // Single capped preview (OneLink summary), not a grid/list card.
  fillWidth: false,
};
